import { HydratedRouter } from "react-router/dom";
import { startTransition, StrictMode, useEffect } from "react";
import {
  useLocation,
  createRoutesFromChildren,
  useNavigationType,
  matchRoutes,
} from "react-router";
import * as Sentry from "@sentry/react";
import { hydrateRoot } from "react-dom/client";
import { ApolloProvider } from "@apollo/client/react";
import { getServerClient } from "./graphql";
import {
  OAUTH_REPLAY_BLOCK_SELECTORS,
  oauthScrubEventProcessor,
  oauthSentryScrubOptions,
  replayAllowedForPageLoad,
  scrubReplayRecordingEvent,
} from "./utils/oauth-url-redaction";
if (webConfig.environment === "production") {
  Sentry.init({
    dsn: "https://27f9d83810da7100c57f985afc6a2ab8@o457803.ingest.us.sentry.io/4508953854869504",
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    environment: webConfig.environment,
    release: webConfig.revision,
    // MCP OAuth (tmcp U1, Codex r1 G-F1): no consent txn / authorize
    // parameters in events, transactions, breadcrumbs or replays.
    ...oauthSentryScrubOptions,
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      // Codex r2 R2-F1 / r3 R3-F1: Replay's recorder writes
      // window.location.href into a Meta event and snapshots the DOM outside
      // every scrubbing hook, so three layers keep OAuth flows out of it:
      // 1. a page load whose (decoded, lower-cased) URL mentions `oauth` or
      //    `txn=` never installs Replay (replayAllowedForPageLoad);
      // 2. the consent card root is blocked (`block` below +
      //    data-sentry-block on the element), so its DOM — the hidden txn —
      //    is never serialised whatever the navigation timing;
      // 3. client navigation into a flow stops Replay (root.tsx,
      //    useStopReplayOnOauthNavigation).
      // maskAllInputs stays at its default (true).
      ...(replayAllowedForPageLoad(window.location.href)
        ? [
            Sentry.replayIntegration({
              block: OAUTH_REPLAY_BLOCK_SELECTORS,
              beforeAddRecordingEvent: (event) =>
                scrubReplayRecordingEvent(event, window.location.pathname),
            }),
          ]
        : []),
    ],
    // ...
  });
  // Replay events (their `urls` list) skip the before* hooks.
  Sentry.addEventProcessor(oauthScrubEventProcessor);
}

startTransition(() => {
  const client = getServerClient();
  hydrateRoot(
    document,
    <StrictMode>
      <ApolloProvider client={client}>
        <HydratedRouter />
      </ApolloProvider>
    </StrictMode>,
  );
});
